/**
 * Alpine component on the customer's configurator. The inputs are real and post with
 * the response form, so this only adds the steppers and the live total. It never
 * works out a price: the server quotes, and checks the total on accept as well.
 */
const QUOTE_DELAY_MS = 250;

export function steppedQuantity(value, step, min, max) {
  const current = Number.parseInt(value, 10);
  if (Number.isNaN(current)) return min;
  return Math.min(max, Math.max(min, current + step));
}

// A pick-one group has one stepper in view, so its value follows the customer to the
// next option when that option allows it.
export function carriedQuantity(value, own, min, max) {
  const text = String(value);
  const carried = Number(text);
  return /^\d+$/.test(text) && carried >= min && carried <= max ? carried : own;
}

export function firstYearCentsIn(html) {
  return String(html).match(/data-first-year-cents="(-?\d+)"/)?.[1] ?? null;
}

/**
 * One quote in flight, for the latest state only. Busy starts at the change, not at
 * the request, so Accept cannot slip through the debounce.
 */
export function quoteQueue({
  send,
  apply,
  onBusy,
  delay = QUOTE_DELAY_MS,
  setTimer = setTimeout,
  clearTimer = clearTimeout
}) {
  let latest = 0;
  let timer = null;
  let inFlight = null;
  let busy = false;

  const setBusy = (value) => {
    if (busy === value) return;
    busy = value;
    onBusy(value);
  };

  const run = (id) => {
    inFlight = new AbortController();
    send(inFlight.signal).then(
      (result) => {
        if (id !== latest) return;
        apply(result);
        setBusy(false);
      },
      () => {
        // The server's check on accept still stands behind a quote that failed.
        if (id === latest) setBusy(false);
      }
    );
  };

  return {
    request() {
      latest += 1;
      const id = latest;
      clearTimer(timer);
      inFlight?.abort();
      setBusy(true);
      timer = setTimer(() => run(id), delay);
    }
  };
}

export default function registerProposalConfigurator(Alpine) {
  Alpine.data("proposalConfigurator", ({ quoteUrl, formId }) => {
    const form = () => document.getElementById(formId);

    const send = async (signal) => {
      const headers = { Accept: "text/vnd.turbo-stream.html" };
      const token = document.querySelector("meta[name='csrf-token']")?.content;
      if (token) headers["X-CSRF-Token"] = token;

      const response = await fetch(quoteUrl, {
        method: "POST",
        body: new FormData(form()),
        credentials: "same-origin",
        headers,
        signal
      });
      const isStream = response.headers.get("Content-Type")?.includes("turbo-stream");
      return isStream ? response.text() : null;
    };

    const apply = (html) => {
      if (!html) return;
      const total = firstYearCentsIn(html);
      const shown = form()?.querySelector("input[name='displayed_first_year_cents']");
      if (total && shown) shown.value = total;
      window.Turbo.renderStreamMessage(html);
    };

    const onBusy = (busy) => {
      for (const element of form()?.elements ?? []) {
        if (element.matches("button[type='submit']")) element.disabled = busy;
      }
    };

    const queue = quoteQueue({ send, apply, onBusy });

    return {
      init() {
        this.$root.querySelector("[data-update-total]")?.remove();
        this.syncSteppers();
        this.$root.dataset.configuratorReady = "true";
      },

      stepperFor(input) {
        return this.$root.querySelector(`[data-stepper="${input.value}"]`);
      },

      changed(event) {
        const input = event.target;
        if (input.type === "radio") this.carry(input);
        this.syncSteppers();
        queue.request();
      },

      // Runs before the steppers move, while the old option's is still in view.
      carry(radio) {
        const next = this.stepperFor(radio);
        const options = this.$root.querySelectorAll(`[data-option-input][data-group="${radio.dataset.group}"]`);
        const current = [...options]
          .map((option) => this.stepperFor(option))
          .find((stepper) => stepper && !stepper.hidden);
        if (!next || !current || next === current) return;

        const { min, max, own } = next.dataset;
        next.querySelector("input").value = carriedQuantity(
          current.querySelector("input").value,
          Number(own),
          Number(min),
          Number(max)
        );
      },

      syncSteppers() {
        for (const input of this.$root.querySelectorAll("[data-option-input]")) {
          const stepper = this.stepperFor(input);
          if (!stepper) continue;
          stepper.hidden = !input.checked;
          stepper.querySelector("input").disabled = !input.checked;
        }
        for (const stepper of this.$root.querySelectorAll("[data-stepper]")) this.markLimits(stepper);
      },

      // aria-disabled, so a keyboard user at a limit keeps their place on the button.
      markLimits(stepper) {
        const value = Number(stepper.querySelector("input").value);
        const { min, max } = stepper.dataset;
        stepper.querySelector("[data-step='-1']").setAttribute("aria-disabled", String(value <= Number(min)));
        stepper.querySelector("[data-step='1']").setAttribute("aria-disabled", String(value >= Number(max)));
      },

      step(event) {
        const button = event.target.closest("[data-step]");
        if (!button) return;

        const stepper = button.closest("[data-stepper]");
        const input = stepper.querySelector("input");
        const { min, max } = stepper.dataset;
        const value = steppedQuantity(input.value, Number(button.dataset.step), Number(min), Number(max));
        if (String(value) === input.value) return;

        input.value = value;
        this.markLimits(stepper);
        queue.request();
      },

      // With the Update total button gone, Enter here would press Accept.
      enter(event) {
        if (event.target.matches("input")) event.preventDefault();
      }
    };
  });
}
